import React from "react";
import { Box, Flex, Image, Text, Badge, Icon } from "@chakra-ui/react";
import { NavLink } from "react-router-dom";
import { FaStar, FaArrowRight } from "react-icons/fa";

const ProductItem = ({ property, i }) => {
  if (!property) return null;

  const originalPrice = parseFloat(property.originalprice) || 0;
  const descPrice = parseFloat(property.descprice) || 0;
  const discountPct =
    originalPrice > descPrice && originalPrice > 0
      ? Math.round(((originalPrice - descPrice) / originalPrice) * 100)
      : 0;

  return (
    <NavLink to={`/products/view/${property.id}`}>
      <Box
        key={i}
        h="100%"
        bg="#FFFFFF"
        border="1px solid #E2E8F0"
        rounded="xl"
        overflow="hidden"
        p="3.5"
        shadow="0 1px 3px rgba(0,0,0,0.04)"
        transition="all 0.25s cubic-bezier(0.16, 1, 0.3, 1)"
        _hover={{
          transform: "translateY(-4px)",
          shadow: "0 14px 28px -4px rgba(15, 23, 42, 0.12)",
          borderColor: "yellow.400",
        }}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
        role="group"
        cursor="pointer"
      >
        {/* Product Image Stage */}
        <Box
          position="relative"
          h={{ base: "170px", sm: "210px", md: "230px" }}
          bg="#F8FAFC"
          rounded="lg"
          p="3"
          display="flex"
          alignItems="center"
          justifyContent="center"
          overflow="hidden"
          mb="3"
        >
          <Image
            src={property.imagePath}
            alt={property.booktitle}
            maxH="100%"
            maxW="100%"
            objectFit="contain"
            transition="transform 0.35s ease"
            _groupHover={{ transform: "scale(1.06)" }}
            fallbackSrc="https://navneetlawstudio.com/logo.png"
            loading="lazy"
          />

          {/* Discount Badge */}
          {discountPct > 0 && (
            <Badge
              position="absolute"
              top="2.5"
              left="2.5"
              bg="red.500"
              color="white"
              fontSize="11px"
              fontWeight="bold"
              px="2"
              py="0.5"
              rounded="md"
              shadow="sm"
            >
              {discountPct}% OFF
            </Badge>
          )}

          {/* Star Rating Badge */}
          <Flex
            position="absolute"
            top="2.5"
            right="2.5"
            align="center"
            gap="1"
            bg="white"
            px="2"
            py="0.5"
            rounded="full"
            shadow="sm"
            border="1px solid #F1F5F9"
          >
            <Icon as={FaStar} color="yellow.400" boxSize="11px" />
            <Text fontSize="11px" fontWeight="bold" color="gray.800">
              {property.hidden_stars || property.stars || "4.8"}
            </Text>
          </Flex>
        </Box>

        {/* Product Meta */}
        <Box flex="1" display="flex" flexDirection="column" justifyContent="space-between">
          <Box>
            {property.maincatname && (
              <Text
                fontSize="11px"
                fontWeight="700"
                color="#2563EB"
                textTransform="uppercase"
                letterSpacing="0.5px"
                mb="1"
              >
                {property.maincatname}
              </Text>
            )}

            <Text
              fontSize={{ base: "13.5px", md: "14.5px" }}
              fontWeight="600"
              color="gray.900"
              lineHeight="1.35"
              noOfLines={2}
              mb="2"
              _groupHover={{ color: "#1D4ED8" }}
              transition="color 0.2s"
            >
              {property.booktitle}
            </Text>

            {property.author && (
              <Text fontSize="12px" color="gray.500" noOfLines={1} mb="2">
                By {property.author}
              </Text>
            )}
          </Box>

          {/* Price & Action Row */}
          <Box pt="2" borderTop="1px solid #F1F5F9" mt="2">
            <Flex align="baseline" justify="space-between" mb="2.5">
              <Flex align="baseline" gap="2" wrap="wrap">
                <Text fontSize={{ base: "17px", md: "19px" }} fontWeight="800" color="gray.900">
                  ₹{property.descprice}
                </Text>
                {originalPrice > descPrice && (
                  <Text fontSize="13px" color="gray.400" as="del">
                    ₹{property.originalprice}
                  </Text>
                )}
              </Flex>

              <Badge colorScheme="green" variant="subtle" fontSize="11px" px="1.5" py="0.5" rounded="md">
                In Stock
              </Badge>
            </Flex>

            <Flex
              w="100%"
              py="2"
              px="3"
              rounded="lg"
              bg="#0F172A"
              color="white"
              align="center"
              justify="center"
              gap="1.5"
              fontSize="12.5px"
              fontWeight="700"
              _groupHover={{ bg: "#2563EB", shadow: "md" }}
              transition="all 0.2s"
            >
              <span>View Details</span>
              <Icon
                as={FaArrowRight}
                boxSize="11px"
                _groupHover={{ transform: "translateX(3px)" }}
                transition="transform 0.2s"
              />
            </Flex>
          </Box>
        </Box>
      </Box>
    </NavLink>
  );
};

export default ProductItem;
